import React, {useEffect, useState} from 'react';
import {RouteComponentProps} from "react-router-dom";
import {ActionProps, ActionResponse, AddSubmissionRequest} from "../../backend/model";
import {Button, ExpandableSection, FormField, Header} from "@amzn/awsui-components-react";
import ActionRow from "./ActionRow";
import {fetchActions, createSubmission} from "../../backend/backend";
import {getApprovalUrl} from "../../backend/internalUrls";
import Input from "@amzn/awsui-components-react/polaris/input";
import AjaxStatusIndicator from "../basic/AjaxStatusIndicator";
import {parseErrorMessage} from "../../utils/errors";

export interface MappingsPanelProps {
    history: RouteComponentProps["history"];
}

const MappingsPanel: React.FC<MappingsPanelProps> = (props) => {
    const [reference, setReference] = useState<string>("")
    const [actionsData, setActionsData] = useState<ActionResponse | null>(null)
    const [actions, setActions] = useState<ActionProps[]>([])
    const [approvers, setApprovers] = useState<string>("")
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    useEffect(() => {
        setIsLoading(true);
        fetchActions()
            .then(({data}) => {
                setActionsData(data)
                const [actionName, actionProps] = Object.entries(data.actions)[0]
                setActions([{
                    actionName,
                    actionParamNames: actionProps.input.map(actionParameters => actionParameters.actionParameterName),
                    actionParamValues: actionProps.input.map(() => '')
                }])
            })
            .catch(error => {
                setErrorMessages([parseErrorMessage(error)])
                console.error(error)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, []);

    async function createSubmissionTask(content: AddSubmissionRequest) {
        setIsLoading(true)
        try {
            const response = (await createSubmission(content)).data
            return response;
        } catch (error: any) {
            setErrorMessages([parseErrorMessage(error)])
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    function setSelectedAction(i: number, selectedAction: string) {
        let actionsCopy = [...actions]
        actionsCopy[i].actionName = selectedAction
        let newActionParamNames: string[] = []
        let newActionParamValues: string[] = []
        actionsData!.actions[selectedAction].input.forEach((actionParam) => {
            newActionParamNames.push(actionParam.actionParameterName)
            newActionParamValues.push('')
        })
        actionsCopy[i].actionParamNames = newActionParamNames
        actionsCopy[i].actionParamValues = newActionParamValues
        setActions(actionsCopy)
    }

    function setActionParamValues(actionIndex: number, actionParamValuesIndex: number, fieldValue: string) {
        let actionsCopy = [...actions]
        actionsCopy[actionIndex].actionParamValues[actionParamValuesIndex] = fieldValue
        setActions(actionsCopy)
    }

    function getStepOptions(i: number, fieldName: string) {
        let arr = []
        for (let j = 0; j < i; j++) {
            let sameParamExist = false
            let actionName = actions[j].actionName
            actionsData!.actions[actionName].output.forEach((value) => {
                if (value.actionParameterName === fieldName) sameParamExist = true
            })
            if (sameParamExist) arr.push({value: "from Step " + j})
        }
        return arr
    }

    function deleteAction() {
        let actionsCopy = [...actions]
        actionsCopy.pop()
        setActions(actionsCopy)
    }

    function submitActions() {
        createSubmissionTask({actions, reference, approvers}).then(data => {
            data ? props.history.push(getApprovalUrl(data.id)) : console.error("data undefined")
        }).catch(error => {
            console.error(error)
        })
    }

    return (
        <>
            { actionsData &&
            <ExpandableSection
                id='InputPanel'
                variant='container'
                header={<Header variant='h2'>Input</Header>}
                defaultExpanded>
                <FormField id="reference" label='SIM Issue'>
                    <Input
                        autoFocus
                        value={reference || ''}
                        onChange={({detail}) => setReference(detail.value)}
                        inputMode={"url"}
                    />
                </FormField>
                <br/>
                <b>Actions</b>
                {actions.map((action, i) => (
                    <ActionRow
                        key={i}
                        selectedActionName={action.actionName}
                        selectedActionParamNames={action.actionParamNames}
                        selectedActionParamValues={action.actionParamValues}
                        selectableActionNames={Object.keys(actionsData.actions)}
                        setSelectedAction={(selectedAction) => setSelectedAction(i, selectedAction)}
                        setActionParamValues={(index, fieldValue) => setActionParamValues(i, index, fieldValue)}
                        getStepOptions={(fieldName) => getStepOptions(i, fieldName)}
                        isEditable={true}
                    />
                ))}
                <br/>
                <Button
                    variant="primary"
                    onClick={
                        () => {
                            let actionsCopy = [...actions]
                            const [actionName, actionProps] = Object.entries(actionsData.actions)[0]
                            actionsCopy.push({
                                actionName,
                                actionParamNames: actionProps.input.map(actionParameters => actionParameters.actionParameterName),
                                actionParamValues: actionProps.input.map(() => '')
                            })
                            setActions(actionsCopy)
                        }
                    }
                >
                    Add New Action
                </Button>
                &nbsp; &nbsp; &nbsp;
                <Button
                    variant="primary"
                    onClick={deleteAction}
                >
                    Delete Action
                </Button>
                <br/>
                <br/>
                <FormField id="approvers" label='Approvers'>
                    <Input
                        autoFocus
                        value={approvers || ''}
                        onChange={({detail}) => setApprovers(detail.value)}
                        inputMode={"text"}
                    />
                </FormField>
                <br/>

                <Button
                    variant="primary"
                    onClick={submitActions}
                >
                    Submit
                </Button>
            </ExpandableSection> }
            <br/>
            <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
        </>
    )
}

export default MappingsPanel
